// Reporter contact prefill — opt-in persistence of name + email.
//
// Backs the "Save your name and email" checkbox under the reporter fields.
// When ticked, a successful submission writes the two values to localStorage
// on the HOST page's origin; the next panel open prefills them and re-ticks
// the box. Clearing the box wipes the stored copy immediately, so the next
// open starts empty.
//
// Storage-shape notes:
//   - Keyed by workspace id, mirroring the widget-config cache in
//     bootstrap.ts, so two widgets embedded on one origin never read each
//     other's values.
//   - Every access is try/catch-guarded. localStorage throws outright in
//     Safari private mode and under some host CSP / partitioning setups, and
//     a contact prefill is a convenience — it must never break the form.
//   - Values are stored verbatim (no encoding beyond JSON). This is the
//     reporter's own contact data, on the reporter's own device, written only
//     after they explicitly opt in.
//
// Sensitive-data-mode: callers must not invoke the write path when the
// workspace has sensitive-data-mode on. That workspace has opted out of
// OnCatch retaining reporter data anywhere, and honouring it only on the
// server while the browser quietly keeps a copy would defeat the intent.
// `ui.ts` gates on this before rendering the checkbox at all.

const CONTACT_PREFILL_PREFIX = 'oncatch_contact_';

/** Mirrors the two reporter fields the checkbox governs. */
export interface StoredContact {
  name: string;
  email: string;
}

function keyFor(workspaceId: string): string {
  return CONTACT_PREFILL_PREFIX + workspaceId;
}

/**
 * Read the saved contact for this workspace. Returns null when nothing is
 * stored, when storage is unavailable, or when the stored blob does not match
 * the expected shape (hand-edited storage, or a future format).
 */
export function readStoredContact(workspaceId: string): StoredContact | null {
  if (!workspaceId) return null;
  try {
    const raw = localStorage.getItem(keyFor(workspaceId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredContact> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    const name = typeof parsed.name === 'string' ? parsed.name : '';
    const email = typeof parsed.email === 'string' ? parsed.email : '';
    // Both empty is indistinguishable from "nothing saved" — treat it as such
    // so an empty blob doesn't tick the checkbox with nothing behind it.
    if (!name && !email) return null;
    return { name, email };
  } catch {
    return null;
  }
}

/**
 * Persist the contact for this workspace. Writing both fields empty clears
 * the entry instead of storing a blank record, keeping `readStoredContact`'s
 * null contract honest.
 */
export function writeStoredContact(workspaceId: string, contact: StoredContact): void {
  if (!workspaceId) return;
  const name = contact.name.trim();
  const email = contact.email.trim();
  if (!name && !email) {
    clearStoredContact(workspaceId);
    return;
  }
  try {
    localStorage.setItem(keyFor(workspaceId), JSON.stringify({ name, email }));
  } catch {
    // Storage blocked or over quota. The reporter's submission is unaffected;
    // they just won't get a prefill next time.
  }
}

/**
 * Probe whether localStorage can actually be written and read back.
 *
 * Called when the reporter TICKS the box, so an unusable store is reported at
 * the moment of the decision instead of failing quietly after they submit.
 * Safari private mode and some partitioned-storage setups throw on access;
 * a few also accept a write and silently drop it, which the read-back catches.
 */
export function isContactStorageAvailable(): boolean {
  const probe = `${CONTACT_PREFILL_PREFIX}__probe`;
  try {
    localStorage.setItem(probe, '1');
    const ok = localStorage.getItem(probe) === '1';
    localStorage.removeItem(probe);
    return ok;
  } catch {
    return false;
  }
}

/** Remove any saved contact for this workspace. Safe to call unconditionally. */
export function clearStoredContact(workspaceId: string): void {
  if (!workspaceId) return;
  try {
    localStorage.removeItem(keyFor(workspaceId));
  } catch {
    // Nothing to clear / storage blocked.
  }
}
